import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useResolvedPath } from 'react-router-dom';
import { Button, Card, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { constructUrl, getParsedQuery, getValidPaginatorSize } from '@/lib/utils';
import { EventModel, ProgramModel } from '@/lib/models';
import { eventsService } from '@/lib/services';
import { useAccount, useHandleError, useQuery } from '@/hooks';
import { AsyncPage, FilterTabs, LinkBox, ListResult, Loader, LoadingButton, Select } from '@/components';

import logoCPE from '@/assets/logos/logo-cpe.gif';

enum VIEW {
	UPCOMING = 'UPCOMING',
	PAST = 'PAST',
}

export const BrowseEvents = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const path = useResolvedPath('').pathname;
	const { account } = useAccount();
	const handleError = useHandleError();

	const searchParams = useQuery();
	const hasQuery = useMemo(() => {
		const paramsToIgnore = ['size', 'view'];

		return (
			Object.entries(getParsedQuery(searchParams)).filter(
				([key, value]) => !paramsToIgnore.includes(key) && value !== ''
			).length > 0
		);
	}, [searchParams]);
	const view = searchParams.get('view') ?? VIEW.UPCOMING;
	const pageRef = useRef(0);
	const programId = searchParams.get('programId') ?? '';
	const size = useMemo(() => parseInt(getValidPaginatorSize(searchParams), 10), [searchParams]);

	const [isLoading, setIsLoading] = useState(false);
	const [programOptions, setProgramOptions] = useState<ProgramModel[]>([]);
	const [events, setEvents] = useState<EventModel[]>([]);
	const [eventsTotal, setEventsTotal] = useState(0);

	const [showCpeDetailsModal, setShowCpeDetailsModal] = useState(false);

	const fetchPrograms = useCallback(async () => {
		if (!account) {
			throw new Error('account is undefined');
		}

		const response = await eventsService.getRelevantEventProgramOptions(view === VIEW.PAST).fetch();
		setProgramOptions(response.programs);
	}, [account, view]);

	const getUpcomingEvents = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await eventsService
				.getUpcomingEvents({
					size,
					page: String(pageRef.current),
					...(programId && { programId }),
				})
				.fetch();

			setEventsTotal(response.totalCount);
			setEvents((previousValue) => {
				if (pageRef.current === 0) {
					return response.events;
				}
				return previousValue.concat(response.events);
			});
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [size, programId, handleError]);

	const getPastEvents = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await eventsService
				.getPastEvents({
					size,
					page: String(pageRef.current),
					...(programId && { programId }),
				})
				.fetch();

			setEventsTotal(response.totalCount);
			setEvents((previousValue) => {
				if (pageRef.current === 0) {
					return response.events;
				}
				return previousValue.concat(response.events);
			});
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [size, programId, handleError]);

	useEffect(() => {
		pageRef.current = 0;

		if (view === VIEW.UPCOMING) {
			getUpcomingEvents();
		} else {
			getPastEvents();
		}
	}, [getUpcomingEvents, getPastEvents, view]);

	const handlePageSizeChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { size: currentTarget.value }, search), { replace: true });
	};

	const handleFilterTabsChange = (value: VIEW) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { view: value }, search));
	};

	const handleProgramSelectChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { programId: currentTarget.value }, search));
	};

	const handleShowMoreButtonClick = () => {
		pageRef.current += 1;
		if (view === VIEW.UPCOMING) {
			getUpcomingEvents();
		} else {
			getPastEvents();
		}
	};

	return (
		<>
			<Helmet>
				<title>Hackett Connect - Events - Browse Events</title>
			</Helmet>

			<Modal
				centered
				show={showCpeDetailsModal}
				onHide={() => {
					setShowCpeDetailsModal(false);
				}}
			>
				<Modal.Header closeButton>
					<Modal.Title>Learn More</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="font-weight-semi-bold">
						Most states require continuing professional education (CPE) for CPAs to maintain active
						licenses. NASBA's Regulatory Compliance Services division offers several programs that assist
						state boards and their licensees by determining high quality CPE providers. The Hackett Group is
						registered with the National Association of State Boards of Accountancy (NASBA) as a sponsor of
						continuing professional education on the National Registry of CPE Sponsors. State Boards of
						Accountancy have the final authority on the acceptance of individual course for CPE credit.
					</p>
					<p>
						Complaints regarding registered sponsors may be submitted to the National Registry of CPE
						Sponsors through its website:{' '}
						<a
							href="https://www.nasbaregistry.org/"
							title="Nasba Registry"
							target="_blank"
							rel="noreferrer"
						>
							www.nasbaregistry.org
						</a>
						. For more information regarding administrative policies such as complaint and refund, please
						contact our offices at 770-225-3600.
					</p>
					<p>
						<span className="font-weight-semi-bold">Program Prerequisites:</span> You must be a member of
						Hackett's Business Advisory Service program and must be employed by the member company
						participating in the membership.
					</p>
					<p className="mb-0">
						<span className="font-weight-semi-bold">How to Register:</span> Find an event in the Upcoming
						Events calendar that relates to your topic area for continuing education and click on the event
						title. From that event, you will find more information on the registration process.
					</p>
				</Modal.Body>
			</Modal>

			<Container className="pt-7">
				<Row>
					<Col>
						<div className="d-flex align-items-center justify-content-between">
							<h1 className="mb-0 text-primary">{isLoading ? 'Loading...' : 'Events'}</h1>
						</div>
					</Col>
				</Row>
			</Container>
			<Container className="py-7">
				<Row>
					<Col xl={8} className="mb-6">
						<FilterTabs
							className="mb-6"
							value={view}
							tabs={[
								{ title: 'Upcoming Events', value: VIEW.UPCOMING },
								{ title: 'Past Events', value: VIEW.PAST },
							]}
							onChange={handleFilterTabsChange}
							aside={
								<div className="d-flex align-items-center">
									<Form.Label className="mb-0">Page Size</Form.Label>
									<Select variant="secondary" value={size} onChange={handlePageSizeChange}>
										<option value="10">10</option>
										<option value="20">20</option>
										<option value="50">50</option>
									</Select>
								</div>
							}
						/>
						{!isLoading && events.length === 0 && (
							<p className="mb-6 text-muted text-center">
								<strong>No Results</strong>
							</p>
						)}
						{isLoading && events.length <= 0 && (
							<div className="mb-8 d-flex justify-content-center">
								<Loader />
							</div>
						)}
						{events.map((event, index) => {
							return (
								<ListResult
									key={`${event.eventId}-${index}`}
									category={event.eventTypeDesc}
									date={`${event.durationDescription ?? ''}`}
									to={`${path}/${event.eventId}`}
									title={event.name}
									description={event.listDescription}
									imageSrc={event.imageUrl}
								/>
							);
						})}
						<hr className="mb-8" />
						<div className="d-flex align-items-center justify-content-end">
							<span className="text-muted mr-4">
								Showing 1-{events.length}
								{hasQuery && <>&nbsp;of {eventsTotal}</>}
							</span>
							{events.length < eventsTotal && (
								<LoadingButton
									isLoading={isLoading}
									variant="outline-info"
									onClick={handleShowMoreButtonClick}
								>
									Show {size} More
								</LoadingButton>
							)}
						</div>
					</Col>
					<Col xl={4}>
						<Card className="mb-2">
							<Card.Body>
								<div className="mb-4 d-flex align-items-center justify-content-between">
									<Card.Title className="mb-0">Filters</Card.Title>
								</div>
								<AsyncPage fetchData={fetchPrograms}>
									<Form.Group>
										<Form.Label>Program</Form.Label>
										<Select value={programId} onChange={handleProgramSelectChange}>
											<option value="">All Programs</option>;
											{programOptions.map((po) => {
												return (
													<option key={po.programId} value={po.programId}>
														{po.name}
													</option>
												);
											})}
										</Select>
									</Form.Group>
								</AsyncPage>
							</Card.Body>
						</Card>
						<LinkBox
							className="mb-2"
							sections={[
								{
									title: "Can't find what you're looking for?",
									links: [{ title: 'Contact Hackett', to: '/contact', showIcon: true }],
								},
							]}
						/>
						<Card className="mb-2">
							<Card.Body>
								<p className="mb-2">
									The Hackett Group is registered with the National Association of State Boards of
									Accountancy (NASBA) as a sponsor of continuing professional education on the
									National Registry of CPE Sponsors. State boards of accountancy have final authority
									on the acceptance of individual courses for CPE credit. Complaints regarding
									registered sponsors may be addressed to National Registry of CPE Sponsors, 150
									Fourth Avenue North, Nashville, TN 37219-2417. Web site:{' '}
									<a
										href="http://www.nasba.org"
										title="Nasba website"
										target="_blank"
										rel="noreferrer"
									>
										www.nasba.org
									</a>
								</p>
								<div className="d-flex align-items-center justify-content-between">
									<Button
										variant="link"
										onClick={() => {
											setShowCpeDetailsModal(true);
										}}
									>
										Learn More
									</Button>
									<img className="d-block" src={logoCPE} alt="logo CPE" width={100} />
								</div>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</Container>
		</>
	);
};
